/* global Temporal */
import "temporal-polyfill/global";
import { useCallback, useMemo, useState } from "react";
import { ExpandableTable } from "../../../components/Table/ExpandableTable";
import FileDownloadOffIcon from "@mui/icons-material/FileDownloadOff";
import { EMPTY_DATA_STRING } from "./DailyReport";
import { scalingBiasCorrection } from "../../../types/Count";
import { PipelineMetricMap } from "../../../types/Pipeline";
import DownloadButton from "../../../components/Buttons/DownloadButton";
import { useQueryClient } from "@tanstack/react-query";
import { useUserContext } from "../../../providers/useUserProvider";
import { useApiClient } from "../../../providers/ApiClientProvider";
import VideoModalLayout from "../../../features/videoModal/layouts/VideoModalLayout";
import { createUrlWithQuery } from "../../../hooks/utils";
import IconButton from "@mui/material/IconButton";
import { Tooltip } from "@mui/material";
import { EnabledVideoModalButton, DisabledVideoModalButton } from "../../../features/videoModal/layouts/ModalButton";
import {
    toTemporalZonedDateTime,
    startOf,
    endOf,
    addDays,
    toDate,
    getMillisecondsDifference,
} from "../../../shared/lib/temporal";

/**
 * Helper: Get the timezone abbreviation for a given hour in the specified timezone.
 */
function getHourTimezoneAbbr(hourStart, timezone) {
    try {
        const zdt = toTemporalZonedDateTime(hourStart, timezone);
        const fmt = new Intl.DateTimeFormat("en-US", {
            timeZone: timezone,
            timeZoneName: "short",
        });
        const parts = fmt.formatToParts(toDate(zdt));
        const tzPart = parts.find((p) => p.type === "timeZoneName");
        return tzPart ? tzPart.value : "";
    } catch (e) {
        return "";
    }
}

function formatHHmmPadded(dateTime) {
    return `${String(dateTime.hour).padStart(2, "0")}:${String(dateTime.minute).padStart(2, "0")}`;
}

/**
 * Helper: build a filename and timestamp for a device/video given a timestamp and timezone
 * Local to MinuteReport because it's only used here.
 */
function buildVideoFilename(siteName, deviceName, timestamp, timezone) {
    if (!timestamp) return { filename: null, filenameTimestamp: null };

    const zdt = startOf(toTemporalZonedDateTime(timestamp, timezone), "minute");

    const year = String(zdt.year).padStart(4, "0");
    const month = String(zdt.month).padStart(2, "0");
    const day = String(zdt.day).padStart(2, "0");
    const hour = String(zdt.hour).padStart(2, "0");
    const minute = String(zdt.minute).padStart(2, "0");

    const filenameTimestamp = `${year}${month}${day}T${hour}${minute}`;
    const filename = `${siteName}-${deviceName}-${filenameTimestamp}`;

    return { filename };
}

const getDeviceOrdinal = (displayName) => {
    return parseInt(displayName.replace(/[^\d]/g, ""), 0);
};

/*
 * Fills in empty timestamps in a series of counts with imputed data points.
 *
 * @param {Date[]} hours - An array of Date objects representing the timestamps to fill.
 * @returns {Count[]} - An array of Count objects with imputed data points for missing timestamps.
 */
function fillEmptySeries(hours) {
    return hours.map((timestamp) => ({
        pipelineIds: null,
        timestamp,
        imputed: true,
        count: {},
    }));
}

// Returns an array of Date objects for each local hour start within the selected day,
// accounting for DST transitions (23 or 25 hours possible).
function getDayHourStartTimes(selectedDate, timezone) {
    // Interpret selectedDate as midnight in the given timezone (not UTC)
    const startOfDay = startOf(toTemporalZonedDateTime(selectedDate, timezone), "day");
    const nextDay = addDays(startOfDay, 1);
    const diffMinutes = getMillisecondsDifference(startOfDay, nextDay) / (1000 * 60); // 1380, 1440, or 1500
    const hourCount = Math.round(diffMinutes / 60);
    return Array.from({ length: hourCount }, (_, i) => toDate(startOfDay.add({ hours: i })));
}

const MinuteReport = ({
    selectedDate,
    site,
    deviceCounts,
    devices,
    deploymentStartTime,
    deploymentEndTime,
    setErrorState,
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalVideo, setModalVideo] = useState(null);
    const { timezoneDetails, isVideoModalEnabled } = useUserContext();
    const timezone = timezoneDetails?.timezone || "America/Halifax";

    const apiClient = useApiClient();

    const queryClient = useQueryClient();

    const data = useMemo(() => processDeviceCounts(deviceCounts), [deviceCounts, selectedDate, site]);

    function processDeviceCounts(dcs) {
        let data = new Map();
        const hourStartByTimerange = new Map();
        let type = "hour";

        // Determine floor start for this day based on deploymentStartTime (if provided)
        const dayStart = startOf(toTemporalZonedDateTime(selectedDate, timezone), "day");
        const deploymentStart = deploymentStartTime ? toTemporalZonedDateTime(deploymentStartTime, timezone) : null;
        let floorStart = dayStart;
        if (deploymentStart && Temporal.ZonedDateTime.compare(deploymentStart, dayStart) >= 0) {
            floorStart = deploymentStart;
        }

        // Determine cap end for this day based on deploymentEndTime (if provided)
        const dayEnd = endOf(toTemporalZonedDateTime(selectedDate, timezone), "day");
        const deploymentEnd = deploymentEndTime ? toTemporalZonedDateTime(deploymentEndTime, timezone) : null;
        let capEnd = dayEnd;
        if (deploymentEnd && Temporal.ZonedDateTime.compare(deploymentEnd, dayEnd) === -1) {
            capEnd = deploymentEnd;
        }

        // Pre-populate hour buckets from floor start up to the capped end (handles 23/25-hour days)
        const dayHours = getDayHourStartTimes(selectedDate, timezone).filter((h) => {
            const hZdt = toTemporalZonedDateTime(h, timezone);
            return (
                Temporal.ZonedDateTime.compare(endOf(hZdt, "hour"), floorStart) >= 0 &&
                Temporal.ZonedDateTime.compare(startOf(hZdt, "hour"), capEnd) <= 0
            );
        });
        dayHours.forEach((hourStart) => {
            let hourStartLocal = startOf(toTemporalZonedDateTime(hourStart, timezone), "hour");
            if (deploymentStart && Temporal.ZonedDateTime.compare(deploymentStart, hourStartLocal) === 1) {
                hourStartLocal = startOf(deploymentStart, "minute");
            }
            let hourEndLocal = endOf(hourStartLocal, "hour");
            if (
                deploymentEnd &&
                Temporal.ZonedDateTime.compare(deploymentEnd, hourStartLocal) === 1 &&
                Temporal.ZonedDateTime.compare(deploymentEnd, hourEndLocal) === -1
            ) {
                hourEndLocal = endOf(deploymentEnd, "minute");
            }
            const startTime = formatHHmmPadded(hourStartLocal);
            const endTime = formatHHmmPadded(hourEndLocal);
            const tzAbbr = getHourTimezoneAbbr(hourStart, timezone);
            const timerange = `${startTime} - ${endTime} (${tzAbbr})`;

            if (!data.has(timerange)) {
                data.set(timerange, {});
            }
            hourStartByTimerange.set(timerange, hourStart);
        });

        Object.values(dcs).forEach((deviceCount) => {
            if (deviceCount.counts.length === 0) {
                deviceCount.counts = fillEmptySeries(dayHours);
            }
            deviceCount.counts.forEach((count) => {
                let hourStartLocal = startOf(toTemporalZonedDateTime(count.timestamp, timezone), "hour");
                if (deploymentStart && Temporal.ZonedDateTime.compare(deploymentStart, hourStartLocal) === 1) {
                    hourStartLocal = endOf(deploymentStart, "minute");
                }
                let hourEndLocal = endOf(hourStartLocal, "hour");
                if (
                    deploymentEnd &&
                    Temporal.ZonedDateTime.compare(deploymentEnd, hourStartLocal) === 1 &&
                    Temporal.ZonedDateTime.compare(deploymentEnd, hourEndLocal) === -1
                ) {
                    hourEndLocal = endOf(deploymentEnd, "minute");
                }
                const startTime = formatHHmmPadded(hourStartLocal);
                const endTime = formatHHmmPadded(hourEndLocal);
                const tzAbbr = getHourTimezoneAbbr(count.timestamp, timezone);
                const timerange = `${startTime} - ${endTime} (${tzAbbr})`;
                const timeRangeData = data.get(timerange) || {};
                const upstream = count.instant ? count.instant.Upstream?.Predicted || 0 : EMPTY_DATA_STRING;
                const downstream = count.instant ? count.instant.Downstream?.Predicted || 0 : EMPTY_DATA_STRING;

                data.set(timerange, {
                    ...timeRangeData,
                    [deviceCount.device.name]: {
                        upstream: upstream,
                        downstream: downstream,
                        type: type,
                    },
                });
            });
        });

        const final = Array.from(data, ([key, value]) => {
            const mapped = hourStartByTimerange.get(key);
            let hourStartIsoValue;

            if (mapped) {
                // Use the directly mapped hour start
                hourStartIsoValue = toDate(startOf(toTemporalZonedDateTime(mapped, timezone), "hour")).toISOString();
            } else {
                // Reconstruct from timerange string (format: "HH:mm - HH:mm (TZ)")
                const [startTimeStr] = key.split(" - ");
                const [hStr, mStr] = startTimeStr.split(":");

                // Create the start moment in the selected date in the timezone
                const dayStartZdt = startOf(toTemporalZonedDateTime(selectedDate, timezone), "day");

                // Add hours and minutes using .with() to stay in the correct date/timezone context
                const withTimeZdt = dayStartZdt.with({
                    hour: Number(hStr),
                    minute: Number(mStr),
                    second: 0,
                    millisecond: 0,
                });

                // Start of that hour
                const computedStartZdt = startOf(withTimeZdt, "hour");
                hourStartIsoValue = toDate(computedStartZdt).toISOString();
            }

            return {
                id: key,
                timerange: key,
                hourStartIso: hourStartIsoValue,
                ...value,
            };
        });
        return final;
    }

    const processApiData = useCallback(
        (deviceCountData, timeFormat = "HH:mm", isSubRow = false) => {
            let combinedData = new Map();

            deviceCountData.forEach((deviceData) => {
                if (!deviceData || !deviceData.countData || deviceData.countData.length === 0) return;

                deviceData.countData.forEach((count) => {
                    if (!count.timestamp) return;

                    const zdtStart = startOf(toTemporalZonedDateTime(count.timestamp, timezone), "minute");
                    let startTime = formatHHmmPadded(zdtStart);
                    if (timeFormat === "mm") {
                        startTime = String(zdtStart.minute).padStart(2, "0");
                    }

                    const timeRangeData = combinedData.get(startTime) || {};
                    combinedData.set(startTime, {
                        ...timeRangeData,
                        [deviceData.device.name]: {
                            upstream: count.instant.Upstream.Predicted,
                            downstream: count.instant.Downstream.Predicted,
                            timestamp: count.timestamp,
                            type: "minute",
                            deviceId: deviceData.device.id,
                        },
                    });
                });
            });

            let finalData = Array.from(combinedData, ([key, value]) => ({
                id: key,
                timerange: key,
                ...value,
            }));
            if (isSubRow) {
                finalData.sort((a, b) => parseInt(a.timerange, 10) - parseInt(b.timerange, 10));
            }
            return finalData;
        },
        [site, timezone],
    );

    const columns = useMemo(() => {
        const tableBodyCellProps = {
            sx: {
                whiteSpace: "nowrap",
                textAlign: "center",
                borderLeft: "2px solid #e0e0e0",
            },
        };
        const tableHeaderCellProps = {
            align: "center",
            sx: {
                whiteSpace: "nowrap",
                textAlign: "center",
                borderLeft: "2px solid #e0e0e0",
            },
        };

        const baseColumns = [
            {
                header: new Intl.DateTimeFormat("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                }).format(selectedDate instanceof Date ? selectedDate : new Date(selectedDate)),
                columns: [
                    {
                        accessorKey: "timerange",
                        header: "Time Range",
                        size: 25,
                        muiTableBodyCellProps: tableBodyCellProps,
                        muiTableHeadCellProps: tableHeaderCellProps,
                    },
                ],
                muiTableBodyCellProps: tableBodyCellProps,
                muiTableHeadCellProps: tableHeaderCellProps,
            },
        ];

        function renderUnidirectional(device) {
            const direction = device.configs?.[0]?.directions?.dir1 === "UPSTREAM" ? "upstream" : "downstream";
            return {
                header: device.displayName,
                muiTableHeadCellProps: {
                    align: "center",
                },
                muiTableBodyCellProps: tableBodyCellProps,
                muiTableHeadCellProps: tableHeaderCellProps,
                columns: [
                    {
                        header: "Counts",
                        size: 25,
                        accessorFn: (row) => {
                            const deviceData = row[device.name];
                            if (!deviceData) return EMPTY_DATA_STRING;
                            const value = direction === "upstream" ? deviceData.upstream : deviceData.downstream;
                            return value !== undefined && value !== null ? value : EMPTY_DATA_STRING;
                        },
                        id: `${device.name}-${direction}`,
                        muiTableBodyCellProps: tableBodyCellProps,
                        muiTableHeadCellProps: tableHeaderCellProps,
                    },
                    {
                        header: "Video",
                        size: 25,
                        accessorKey: `${device.name}-vid`,
                        Cell: ({ row }) => {
                            const deviceData = row.original[device.name];
                            if (!deviceData) {
                                return null;
                            }
                            if (!deviceData?.timestamp || deviceData.type !== "minute") return null;
                            if (isVideoModalEnabled && isVideoModalEnabled()) {
                                return renderVideoModal(device);
                            }
                            return renderVideoDownload(device, deviceData);
                        },
                    },
                ],
            };
        }

        function renderVideoOnly(device) {
            return {
                header: device.displayName,
                muiTableBodyCellProps: tableBodyCellProps,
                muiTableHeadCellProps: tableHeaderCellProps,
                columns: [
                    {
                        header: "Video",
                        size: 75,
                        muiTableBodyCellProps: tableBodyCellProps,
                        muiTableHeadCellProps: tableHeaderCellProps,
                        accessorKey: `${device.name}-vid`,
                        Cell: ({ row }) => {
                            const deviceData = row.original[device.name];
                            if (!deviceData) {
                                if (isVideoModalEnabled && isVideoModalEnabled()) {
                                    return <DisabledVideoModalButton />;
                                } else {
                                    return (
                                        <Tooltip title="No video available">
                                            <IconButton aria-label="video-download-off" disabled>
                                                <FileDownloadOffIcon />
                                            </IconButton>
                                        </Tooltip>
                                    );
                                }
                            }
                            if (!deviceData?.timestamp || deviceData.type !== "minute") return null;
                            if (isVideoModalEnabled && isVideoModalEnabled()) {
                                return renderVideoModal(device);
                            }
                            return renderVideoDownload(device, deviceData);
                        },
                    },
                ],
            };
        }

        function renderDefault(device) {
            return {
                header: device.displayName,
                muiTableBodyCellProps: tableBodyCellProps,
                muiTableHeadCellProps: tableHeaderCellProps,
                columns: [
                    {
                        header: "Upstream",
                        size: 25,
                        accessorFn: (row) => {
                            const show =
                                row[device.name]?.upstream !== undefined && row[device.name]?.upstream !== null;
                            if (show) {
                                return row[device.name].upstream;
                            }
                            return EMPTY_DATA_STRING;
                        },
                        id: `${device.name}-upstream`,
                        muiTableBodyCellProps: tableBodyCellProps,
                        muiTableHeadCellProps: tableHeaderCellProps,
                    },
                    {
                        header: "Downstream",
                        size: 25,
                        accessorFn: (row) =>
                            row[device.name]?.downstream !== undefined && row[device.name]?.downstream !== null
                                ? row[device.name]?.downstream
                                : EMPTY_DATA_STRING,
                        id: `${device.name}-downstream`,
                        muiTableBodyCellProps: tableBodyCellProps,
                        muiTableHeadCellProps: tableHeaderCellProps,
                    },
                    {
                        header: "Video",
                        size: 25,
                        accessorKey: `${device.name}-vid`,
                        Cell: ({ row }) => {
                            const deviceData = row.original[device.name];
                            if (!deviceData) {
                                return null;
                            }
                            if (!deviceData?.timestamp || deviceData.type !== "minute") return null;
                            if (isVideoModalEnabled && isVideoModalEnabled()) {
                                return renderVideoModal(device, deviceData);
                            }
                            return renderVideoDownload(device, deviceData);
                        },
                        muiTableBodyCellProps: tableBodyCellProps,
                        muiTableHeadCellProps: tableHeaderCellProps,
                    },
                ],
            };
        }

        function renderVideoModal(device, deviceData) {
            return (
                <EnabledVideoModalButton
                    onClick={() => {
                        setModalVideo({
                            device: device,
                            time: deviceData.timestamp,
                        });
                        setModalOpen(true);
                    }}
                />
            );
        }

        function renderVideoDownload(device, deviceData) {
            const { filename } = buildVideoFilename(site.name, device.name, deviceData.timestamp, timezone);
            return (
                <Tooltip title="Download video">
                    <DownloadButton
                        filename={filename}
                        deviceId={device.id}
                        timestamp={deviceData.timestamp}
                        size="small"
                        setErrorState={setErrorState}
                    />
                </Tooltip>
            );
        }

        if (devices) {
            const sortedDevices = [...devices].sort((a, b) => {
                const ordinalA = getDeviceOrdinal(a.displayName);
                const ordinalB = getDeviceOrdinal(b.displayName);
                return ordinalA - ordinalB;
            });
            const deviceColumns = sortedDevices.map((device) => {
                if (device.configs?.[0].directionality === "UNIDIRECTIONAL") {
                    return renderUnidirectional(device);
                } else if (device.configs?.[0].mode === "VIDEO_ONLY") {
                    return renderVideoOnly(device);
                } else {
                    return renderDefault(device);
                }
            });

            return [...baseColumns, ...deviceColumns];
        }
    }, [devices, data, selectedDate, isVideoModalEnabled]);

    const fetchSubRows = useCallback(
        async (parentRow) => {
            try {
                // Use hourStartIso which was computed with proper timezone context
                // It's a UTC ISO string representing the start of the hour in local time
                const hourStartDate = new Date(parentRow.hourStartIso);
                const hourStartZdt = toTemporalZonedDateTime(hourStartDate, timezone);

                const rowHourStartZdt = startOf(hourStartZdt, "hour");
                let rowHourEndZdt = endOf(rowHourStartZdt, "hour");

                // Check if this hour row is capped by deploymentEndTime
                // (deploymentEndTime may fall within this hour, capping it before the hour end)
                if (deploymentEndTime) {
                    const depEndZdt = toTemporalZonedDateTime(deploymentEndTime, timezone);
                    // If deployment ends within this hour, cap the row end to deployment end
                    if (
                        Temporal.ZonedDateTime.compare(depEndZdt, rowHourStartZdt) === 1 &&
                        Temporal.ZonedDateTime.compare(depEndZdt, rowHourEndZdt) === -1
                    ) {
                        rowHourEndZdt = endOf(depEndZdt, "minute");
                    }
                }

                let startTimestamp = toDate(rowHourStartZdt).toISOString();

                let endTimestamp;
                if (deploymentEndTime) {
                    const depEndZdt = toTemporalZonedDateTime(deploymentEndTime, timezone);
                    const depEndMinuteZdt = endOf(depEndZdt, "minute");
                    const hourEndMinuteZdt = endOf(rowHourEndZdt, "minute");

                    // Cap to min(deploymentEndTime, hourEnd).endOf('minute')
                    const depEndMs = toDate(depEndMinuteZdt).getTime();
                    const hourEndMs = toDate(hourEndMinuteZdt).getTime();
                    endTimestamp =
                        depEndMs < hourEndMs
                            ? toDate(depEndMinuteZdt).toISOString()
                            : toDate(hourEndMinuteZdt).toISOString();
                } else {
                    endTimestamp = toDate(endOf(rowHourEndZdt, "minute")).toISOString();
                }

                const detectionParams = {
                    startTimestamp,
                    endTimestamp,
                    interval: "1m",
                };

                const subRowsData = await Promise.all(
                    devices.map(async (device) => {
                        const minuteCounts = await queryClient.fetchQuery({
                            queryKey: [
                                "minute-detections",
                                device.id,
                                startTimestamp,
                                endTimestamp,
                                deploymentEndTime || "no-deploy-end",
                            ],
                            queryFn: async ({ signal }) => {
                                const response = await apiClient.get(
                                    createUrlWithQuery(`/devices/${device.id}/counts`, {
                                        ...detectionParams,
                                    }),
                                    { signal },
                                );
                                return response.data.counts;
                            },
                        });

                        if (MinuteReport.Error) {
                            setErrorState({
                                hasError: true,
                                message: MinuteReport.Error.message,
                            });
                        }

                        if (!minuteCounts || minuteCounts.length === 0) {
                            const unprocessedVideos = await queryClient.fetchQuery({
                                queryKey: ["unprocessed-videos", device.id, startTimestamp, endTimestamp],
                                queryFn: async () => {
                                    const missingVideos = await apiClient.get(
                                        `/devices/${device.id}/videos?isUnprocessed=1&startTime=${startTimestamp}&endTime=${endTimestamp}`,
                                        { signal: parentRow.signal },
                                    );
                                    return missingVideos.data;
                                },
                            });
                            var unprocessedMinuteRecords = unprocessedVideos?.map((video) => ({
                                timestamp: video.time,
                                instant: {
                                    Upstream: {
                                        Predicted: EMPTY_DATA_STRING,
                                    },
                                    Downstream: {
                                        Predicted: EMPTY_DATA_STRING,
                                    },
                                },
                            }));
                            if (unprocessedMinuteRecords.length > 0) {
                                return {
                                    device,
                                    countData: unprocessedMinuteRecords,
                                };
                            }
                        }

                        const pipelines = [
                            ...new Set(minuteCounts ? minuteCounts.map((count) => count.pipelineID) : []),
                        ];

                        const pipelineMetricsPromise = pipelines.map((pipeline) =>
                            queryClient.fetchQuery({
                                queryKey: ["pipeline-metrics", pipeline],
                                queryFn: ({ signal }) => apiClient.get(`/pipelines/${pipeline}`, { signal }),
                            }),
                        );

                        const pipelineMetricsResult = await Promise.all(pipelineMetricsPromise);

                        const pipelineMetrics = {};

                        pipelines.forEach((pipeline, index) => {
                            pipelineMetrics[pipeline] = pipelineMetricsResult[index]?.metrics || [];
                        });

                        return {
                            device,
                            countData: minuteCounts
                                ? scalingBiasCorrection(minuteCounts, new PipelineMetricMap(pipelineMetrics))
                                : [],
                        };
                    }),
                );
                const processedSubRows = processApiData(subRowsData, "mm", true, "minute");

                return processedSubRows.map((subRow) => ({
                    ...subRow,
                    parentId: parentRow.id,
                }));
            } catch (error) {
                console.log(error);
                setErrorState({
                    hasError: true,
                    message: `unable to load minute-level data`,
                });
                console.log(error);
                return [];
            }
        },
        [devices, selectedDate, data, setErrorState],
    );

    return (
        <>
            <ExpandableTable
                columns={columns}
                data={data}
                fetchSubRows={fetchSubRows}
                noDataMessage="No minute data available for this time range"
            />
            {modalOpen && modalVideo && (
                <VideoModalLayout
                    data-testid="video-modal"
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    video={modalVideo}
                    site={site}
                    setErrorState={setErrorState}
                    contextInfo={{ site: site.displayName }}
                />
            )}
        </>
    );
};

export default MinuteReport;
